import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { useQuery } from '@tanstack/react-query';
import { userAPI, referralAPI, reservationAPI } from '../api/client';
import { track } from '../utils/analytics';
import ReferralCodeSheet from '../components/ReferralCodeSheet';
import Layout from '../components/Layout';
import PointChargeModal from '../components/PointChargeModal';
import WalkingAnimal from '../components/WalkingAnimal';
import QuickHomeCard from '../components/QuickHomeCard';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { useNotification } from '../hooks/useNotification';
import { COMMERCE_ENABLED, REFERRAL_ENABLED } from '../config/features';

function TodayReservations({ userId }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['reservations', userId],
    queryFn: async () => {
      const res = await reservationAPI.getReservations(userId);
      return res.success ? res.data.reservations || [] : [];
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  const reservations = data || [];

  return (
    <div
      onClick={() => navigate('/reservations')}
      className="bg-white rounded-2xl shadow-card p-5 mb-3 cursor-pointer btn-press animate-fade-up stagger-2 hover:shadow-md transition-shadow border border-border"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-charcoal">오늘의 예약</h3>
        <svg className="w-4 h-4 text-sub-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 py-2">
          <div className="w-4 h-4 border-2 border-tayo/30 border-t-tayo rounded-full animate-spin" />
          <span className="text-xs text-sub-text">불러오는 중...</span>
        </div>
      ) : reservations.length === 0 ? (
        <p className="text-sm text-sub-text py-1">예약된 버스가 없습니다</p>
      ) : (
        <div className="space-y-2">
          {reservations.map((r, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                r.direction === 'DOWN' || r.routeName?.includes('하교')
                  ? 'bg-orange-50 text-orange-500'
                  : 'bg-tayo-light text-tayo'
              }`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-charcoal truncate">
                  {r.routeName || r.carNum || '버스'} {r.operateTime || ''}
                </p>
              </div>
              <span className="text-xs font-bold text-tayo bg-tayo-light px-2 py-0.5 rounded-full flex-shrink-0">
                {r.seatNo}번
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshAccess } = useAuthStore();
  const showToast = useToastStore(s => s.showToast);
  const [userInfo, setUserInfo] = useState(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showReferralSheet, setShowReferralSheet] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingRefCode = searchParams.get('ref') || '';
  const { isInstalled, isIOS, canInstall, promptInstall } = useInstallPrompt();
  const { isSupported: notifSupported, isEnabled: notifEnabled, isLoading: notifLoading, requestPermission, disableNotification } = useNotification(user?.userId);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // 구독/접근 상태 최신화
  useEffect(() => {
    if (user?.userId) refreshAccess();
  }, [user?.userId]);

  // 리퍼럴 코드 입력 시트 표시 여부 체크
  useEffect(() => {
    if (!REFERRAL_ENABLED) return;
    if (!user?.userId) return;
    if (pendingRefCode) {
      setShowReferralSheet(true);
      return;
    }
    if (localStorage.getItem('referral_sheet_shown')) return;
    referralAPI.getStatus(user.userId).then(res => {
      if (res.success && !res.data.hasUsedReferral) {
        setShowReferralSheet(true);
        localStorage.setItem('referral_sheet_shown', '1');
      }
    }).catch(() => {});
  }, [user?.userId, pendingRefCode]);

  useEffect(() => {
    if (!user?.userId) return;

    userAPI.getInfo(user.userId).then((res) => {
      if (res.success) {
        setUserInfo(res.data);
      }
    }).catch((err) => {
      console.error('[Dashboard] Failed to fetch user info:', err);
    });
  }, [user?.userId]);

  if (!user) return null;

  const displayName = userInfo?.name || user.name || user.studentId;

  return (
    <Layout title="타요버스" showBottomNav={true}>
      {/* Greeting & Action Buttons */}
      <div className="mb-5 animate-fade-up">
        <h2 className="text-lg sm:text-2xl font-bold text-charcoal mb-3">안녕하세요, {displayName}님 👋</h2>
        <div className="flex items-center gap-2">
          {userInfo?.remainCash != null && (
            <button
              onClick={() => { setShowChargeModal(true); track('point-charge-started'); }}
              className="btn-press flex items-center bg-tayo-light border border-tayo/20 rounded-2xl px-3 py-2 hover:bg-tayo/10 transition-colors cursor-pointer group"
            >
              <svg className="w-4 h-4 text-tayo mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-left">
                <p className="text-[10px] text-tayo font-medium">잔여 포인트</p>
                <p className="text-sm font-bold text-tayo-dark whitespace-nowrap">{userInfo.remainCash}원</p>
              </div>
              <svg className="w-4 h-4 text-tayo/50 ml-1 group-hover:text-tayo transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
          {COMMERCE_ENABLED && (() => {
            const au = user?.accessUntil;
            const hasAccess = au && new Date(au) > new Date();
            const days = hasAccess ? Math.ceil((new Date(au) - new Date()) / 86400000) : 0;
            if (hasAccess) return (
              <button onClick={() => navigate('/pricing')} className="btn-press flex items-center bg-gradient-to-br from-tayo-light to-blue-100 border border-tayo/20 rounded-2xl px-3 py-2 hover:from-blue-100 hover:to-blue-200 transition-all cursor-pointer group">
                <svg className="w-4 h-4 text-tayo mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <div className="text-left"><p className="text-[10px] text-tayo font-medium">타요패스</p><p className="text-sm font-bold text-tayo-dark">{days}일 남음</p></div>
                <svg className="w-3.5 h-3.5 text-tayo/50 ml-1.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            );
            return (
              <button onClick={() => navigate('/pricing')} className="btn-press flex items-center bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl px-3 py-2 hover:from-amber-100 hover:to-orange-100 transition-all cursor-pointer group">
                <svg className="w-4 h-4 text-amber-500 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                <div className="text-left"><p className="text-[10px] text-amber-600 font-medium">타요패스</p><p className="text-sm font-bold text-amber-700">시작하기</p></div>
                <svg className="w-3.5 h-3.5 text-amber-400 ml-1.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            );
          })()}
        </div>
      </div>

      {/* 바로 집가기 카드 */}
      <QuickHomeCard userId={user?.userId} />

      {/* 노선 지도 */}
      <button
        type="button"
        onClick={() => navigate('/route-map')}
        className="mb-3 flex min-h-20 w-full items-center gap-4 rounded-2xl border border-tayo/20 bg-white p-5 text-left shadow-card transition-shadow btn-press animate-fade-up stagger-2 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tayo focus-visible:ring-offset-2"
      >
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-tayo-light to-blue-100">
          <svg className="h-6 w-6 text-tayo" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m9 18-6 3V6l6-3m0 15 6 3m-6-3V3m6 18 6-3V3l-6 3m0 15V6M9 3l6 3" />
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-charcoal">노선 지도</span>
          <span className="mt-1 block text-xs text-sub-text">학교에서 뻗어가는 통학버스 노선을 한눈에 봐요</span>
        </span>
        <svg className="h-5 w-5 flex-shrink-0 text-tayo/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m9 5 7 7-7 7" />
        </svg>
      </button>

      {/* 오늘의 예약 */}
      <TodayReservations userId={user?.userId} />

      {/* Referral Card */}
      {REFERRAL_ENABLED && (
        <div
          onClick={() => navigate('/referral')}
          className="mt-3 bg-white rounded-2xl shadow-card p-5 cursor-pointer btn-press animate-fade-up stagger-6 hover:shadow-md transition-shadow border border-tayo/20"
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-tayo-light to-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-tayo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-charcoal mb-1">친구 초대</h3>
              <p className="text-xs text-sub-text">친구를 초대하면 서로 1주 무료!</p>
            </div>
            <svg className="w-5 h-5 text-tayo/50 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      )}

      {/* Admin Card - admin role일 때만 표시 */}
      {user?.role === 'admin' && (
        <div
          onClick={() => navigate('/admin')}
          className="mt-3 bg-white rounded-2xl shadow-card p-5 cursor-pointer btn-press animate-fade-up stagger-6 hover:shadow-md transition-shadow border border-violet-200"
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-11 h-11 bg-violet-50 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-charcoal mb-1">관리자 페이지</h3>
              <p className="text-xs text-sub-text">시스템 관리 및 모니터링</p>
            </div>
            <svg className="w-5 h-5 text-violet-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      )}

      {/* Notification Card - iOS 미설치 시 숨김 (iOS는 PWA 설치 후에만 알림 가능) */}
      {notifSupported && !notifEnabled && !(isIOS && !isInstalled) && (
        <div
          onClick={async () => {
            if (notifLoading) return;
            const ok = await requestPermission();
            if (ok) {
              showToast('예약 알림이 활성화되었습니다', 'success');
            } else if (window.Notification?.permission === 'denied') {
              showToast('알림 권한이 차단되어 있습니다. 브라우저 설정에서 허용해주세요.', 'error');
            } else {
              showToast('알림 등록에 실패했습니다. 다시 시도해주세요.', 'error');
            }
          }}
          className="mt-3 bg-white rounded-2xl shadow-card p-5 cursor-pointer btn-press animate-fade-up stagger-6 hover:shadow-md transition-shadow border border-tayo/20"
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-11 h-11 bg-tayo-light rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-tayo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-charcoal mb-1">예약 알림 받기</h3>
              <p className="text-xs text-sub-text">자동 예약 결과를 푸시 알림으로 받아요</p>
            </div>
            {notifLoading ? (
              <div className="w-5 h-5 border-2 border-tayo/30 border-t-tayo rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5 text-tayo flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* 알림 활성화 상태 + 끄기 버튼 - iOS 미설치 시 숨김 */}
      {notifSupported && notifEnabled && !(isIOS && !isInstalled) && (
        <div className="mt-3 bg-tayo-light/50 rounded-2xl p-4 animate-fade-up stagger-6 border border-tayo/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-tayo flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-sm text-tayo-dark font-medium">예약 알림 활성화됨</p>
            </div>
            <button
              onClick={async () => {
                if (notifLoading) return;
                const ok = await disableNotification();
                if (ok) showToast('예약 알림이 해제되었습니다', 'info');
              }}
              className="text-xs text-sub-text hover:text-charcoal transition-colors px-2 py-1 rounded-lg hover:bg-white/50"
            >
              끄기
            </button>
          </div>
        </div>
      )}

      {/* App Install Card - standalone 모드가 아닐 때만 표시 */}
      {!isInstalled && (
        <div
          onClick={async () => {
            if (canInstall) {
              const accepted = await promptInstall();
              if (accepted) track('pwa-install');
            } else if (isIOS) {
              showToast('Safari 하단의 공유 버튼 → "홈 화면에 추가"를 선택하세요', 'info');
            } else {
              showToast('Chrome 메뉴(⋮) → "홈 화면에 추가" → "설치"를 선택하세요', 'info');
            }
          }}
          className="mt-3 bg-white rounded-2xl shadow-card p-5 cursor-pointer btn-press animate-fade-up stagger-6 hover:shadow-md transition-shadow border border-tayo/20"
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden">
              <img src="/images/icons/icon-96x96.png" alt="타요버스" className="w-full h-full" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-charcoal mb-1">앱으로 설치하기</h3>
              {isIOS ? (
                <p className="text-xs text-sub-text">
                  설치하면 <strong className="text-tayo">예약 알림</strong>을 받을 수 있어요
                  <br />
                  <span className="text-sub-text/70">공유
                    <svg className="inline w-3.5 h-3.5 text-tayo mx-0.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    → 홈 화면에 추가
                  </span>
                </p>
              ) : (
                <p className="text-xs text-sub-text">홈 화면에 추가하면 더 빠르게 이용해요</p>
              )}
            </div>
            <svg className="w-5 h-5 text-tayo flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
        </div>
      )}

      <WalkingAnimal />
      <PointChargeModal
        isOpen={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        userId={user?.userId}
        onChargeComplete={() => {
          userAPI.getInfo(user.userId).then((res) => {
            if (res.success) setUserInfo(res.data);
          });
        }}
      />
      {REFERRAL_ENABLED && (
        <ReferralCodeSheet
          isOpen={showReferralSheet}
          onClose={(applied) => {
            setShowReferralSheet(false);
            if (pendingRefCode) {
              searchParams.delete('ref');
              setSearchParams(searchParams, { replace: true });
            }
          }}
          userId={user?.userId}
          initialCode={pendingRefCode}
        />
      )}
    </Layout>
  );
}
